import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Card } from '@asia/shared';
import { useBackHandler } from '../../hooks/useBackHandler';
import { CardFace } from './CardFace';
import { CardTile } from './CardSheets';

/** Animated card preview while issue is in flight — float + light sweep. */
function LiveCardPreview({ card }: { card: Card }) {
  return (
    <div className="relative w-[min(100%,320px)] px-2">
      <div
        aria-hidden
        className="issuing-card-glow pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[85%] -translate-x-1/2 -translate-y-1/2 rounded-[40px] bg-accent/40 blur-2xl"
      />
      <div className="issuing-card-float relative overflow-hidden rounded-[24px] shadow-[0_20px_50px_rgba(0,0,0,0.35)]">
        <CardFace card={card} className="w-full" />
        <div
          aria-hidden
          className="issuing-card-shimmer pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/55 to-transparent"
        />
      </div>
    </div>
  );
}

/** Full-screen loading overlay for anything that takes a card round-trip —
 *  issuing by default, or whatever `label` says. Optionally shows a card
 *  preview so the user sees what they're getting while payment confirms. */
export function IssuingOverlay({
  label,
  hint,
  card,
  error,
  onRetry,
  onDismiss,
  showPaymentSafe = false,
  /** Larger animated card face (issue / top-up). Default tile otherwise. */
  animateCard = false,
}: {
  label?: string;
  /** Secondary line under the title. */
  hint?: string | null;
  card?: Card | null;
  error?: string | null;
  onRetry?: () => void;
  onDismiss?: () => void;
  /** Only when a durable payment is retained — otherwise Retry may charge again. */
  showPaymentSafe?: boolean;
  animateCard?: boolean;
} = {}) {
  const { t } = useTranslation();
  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-6 bg-black/[0.4] px-6 backdrop-blur-[10px]">
      {error ? (
        <>
          {card ? (animateCard ? <LiveCardPreview card={card} /> : <CardTile card={card} />) : null}
          <p className="max-w-[300px] text-center text-[15px] leading-[1.44] text-white">{error}</p>
          {onRetry && showPaymentSafe && (
            <p className="max-w-[300px] text-center text-[13px] leading-[1.44] text-white/70">
              {t('cards.paymentSafe')}
            </p>
          )}
          {onRetry && (
            <button
              onClick={onRetry}
              className="flex h-[52px] w-[220px] items-center justify-center rounded-full bg-accent text-[15px] font-bold text-white active:opacity-90"
            >
              {t('cards.retryIssue')}
            </button>
          )}
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-[14px] font-medium text-white/80 active:opacity-70"
            >
              {t('common.done')}
            </button>
          )}
        </>
      ) : (
        <>
          {card && animateCard ? (
            <LiveCardPreview card={card} />
          ) : card ? (
            <CardTile card={card} />
          ) : (
            <span className="size-12 animate-spin rounded-full border-[3px] border-card/30 border-t-white" />
          )}
          <div className="flex max-w-[300px] flex-col items-center gap-2 text-center">
            <p className="text-[18px] font-medium tracking-[-0.3px] text-white">
              {label ?? t('cards.preparing')}
            </p>
            {hint ? (
              <p className="text-[13px] leading-[1.44] text-white/65">{hint}</p>
            ) : null}
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-[14px] font-medium text-white/80 active:opacity-70"
            >
              {t('common.done')}
            </button>
          )}
        </>
      )}
    </div>,
    document.body,
  );
}

const CONFETTI_COLORS = ['#2195f1', '#58b272', '#f7b32b', '#ec3240', '#8ccdff', '#8458db'];

/** Congrats screen with falling confetti — shown once the card is issued;
 *  Continue returns to the Cards screen. */
export function CongratsOverlay({ card, onDone }: { card: Card; onDone: () => void }) {
  const { t } = useTranslation();
  // The native Telegram BackButton finishes the celebration (same as Continue).
  useBackHandler(true, onDone);
  const pieces = useMemo(
    () =>
      Array.from({ length: 70 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        dur: 2.4 + Math.random() * 1.8,
        size: 6 + Math.random() * 8,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]!,
        rot: (Math.random() > 0.5 ? 1 : -1) * (540 + Math.round(Math.random() * 360)),
      })),
    [],
  );

  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-surface px-8">
      {/* confetti */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {pieces.map((p, i) => (
          <span
            key={i}
            className="confetti-piece absolute rounded-[2px]"
            style={{
              left: `${p.left}%`,
              width: p.size,
              height: p.size * 0.45,
              backgroundColor: p.color,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.dur}s`,
              ['--rot' as never]: `${p.rot}deg`,
            }}
          />
        ))}
      </div>

      <div className="relative flex flex-col items-center gap-5 text-center">
        <CardTile card={card} />
        <h1 className="text-[24px] font-medium text-ink">{t('cards.congratsTitle')}</h1>
        <p className="text-[15px] leading-[1.44] text-muted">
          {t('cards.congratsDesc', { label: card.label })}
        </p>
        <button
          onClick={onDone}
          className="mt-3 flex h-[52px] w-[220px] items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white active:opacity-90"
        >
          {t('common.continue')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
